import {
  ClosingRequirement,
  ClosingRequirementResult,
  ClosingRequirementValue,
} from "components/closing-requirements/definitions";
import { Document } from "models/schemas";
import { FileOption } from "./definitions";

export const PROGRESS_UPDATE_DOCUMENT_TYPE = "Progress updates";

export const unmetCount = (
  results: ClosingRequirementResult[] | undefined,
  severity: ClosingRequirementResult["severity"],
) =>
  (results ?? []).filter(
    (result) => !result.met && result.severity === severity,
  ).length;

export const pluralise = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

export const toFileOptions = (
  documents: Document[] | undefined,
): FileOption[] =>
  (documents ?? [])
    .filter(
      (document) =>
        !document.isFolder &&
        !document.deleted &&
        document.documentType !== PROGRESS_UPDATE_DOCUMENT_TYPE,
    )
    .map((document) => ({
      value: document.documentId,
      label: `${document.fileName}${document.extensionType ?? ""}`,
      description: document.documentType,
    }));

export const valueHint = (requirement?: ClosingRequirement): string => {
  const attributes = requirement?.attributes ?? {};
  switch (requirement?.requirementType) {
    case "FILE": {
      const count = Number(attributes.minCount ?? "1");
      return `Select ${pluralise(
        count,
        attributes.documentType ?? "file",
        attributes.documentType ?? "files",
      )} from this record.`;
    }
    case "AMOUNT":
      if (attributes.minValue && attributes.maxValue) {
        return `Between ${attributes.minValue} and ${attributes.maxValue}.`;
      }
      if (attributes.minValue) return `At least ${attributes.minValue}.`;
      if (attributes.maxValue) return `At most ${attributes.maxValue}.`;
      return "Enter an amount.";
    case "TEXT":
      return attributes.minLength
        ? `At least ${attributes.minLength} characters.`
        : "Enter text.";
    case "LINK":
      if (attributes.linkTarget === "EXTERNAL_URL") {
        return "A web address starting with https://";
      }
      if (attributes.linkTarget === "GAIA_OBJECT") {
        return "A link to an audit, notice or litigation, or its ID.";
      }
      return "A web address, or a link to an audit, notice or litigation, or its ID.";
    default:
      return "";
  }
};

export const withValue = <
  K extends Exclude<keyof ClosingRequirementValue, "requirementId">,
>(
  requirementId: string,
  field: K,
  input: ClosingRequirementValue[K] | "" | [],
): ClosingRequirementValue | null => {
  const empty =
    input === undefined ||
    input === "" ||
    input === false ||
    (Array.isArray(input) && input.length === 0);
  return empty ? null : { requirementId, [field]: input };
};
